import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ExternalLink, Search, Loader2 } from "lucide-react";
import useSEO, { orgSchema, breadcrumbSchema } from "@/lib/useSEO";

/**
 * Public SEO directory. Every purchased block is rendered as plain HTML with a
 * real <a href="…"> that passes link equity (dofollow — we don't emit rel=nofollow).
 * The page is fully text-based and rendered client-side but crawlers (Google,
 * Bing) do execute JS and will read this. For maximum SEO we also expose the
 * same data at GET /api/directory.
 */
function truncateWords(text, maxWords = 50) {
  if (!text) return "";
  const words = String(text).split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return words.join(" ");
  return words.slice(0, maxWords).join(" ") + "…";
}

function ensureAbsoluteUrl(u) {
  if (!u) return "#";
  const trimmed = String(u).trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export default function Directory() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useSEO({
    title: "Business Directory — PixelEmpire AI · Do-Follow SEO Backlinks",
    description:
      "Browse every business live on the PixelEmpire AI 100×100 grid. Each listing is a real do-follow SEO backlink from our permanent advertising directory.",
    path: "/directory",
    jsonLd: [
      orgSchema,
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Directory", url: "/directory" },
      ]),
    ],
  });

  useEffect(() => {
    let alive = true;
    http
      .get("/directory", { params: { limit: 500 } })
      .then(({ data }) => {
        if (!alive) return;
        setListings(data.listings || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return listings;
    const needle = q.toLowerCase();
    return listings.filter(
      (l) =>
        (l.brand_name || "").toLowerCase().includes(needle) ||
        (l.description || "").toLowerCase().includes(needle) ||
        (l.hover_slogan || "").toLowerCase().includes(needle),
    );
  }, [listings, q]);

  return (
    <div className="min-h-screen relative z-10">
      <Header />

      <main className="pt-32 pb-24 px-4 max-w-5xl mx-auto" data-testid="directory-page">
        <header className="mb-10">
          <div className="pill" data-testid="directory-pill">
            <span>Directory · Public SEO index</span>
          </div>
          <h1
            className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-white"
            data-testid="directory-h1"
          >
            The PixelEmpire <span className="text-gradient">Business Directory</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[var(--fg-muted)] max-w-3xl">
            Every business below owns a permanent block on our 100×100 pixel grid.
            Each listing is a{" "}
            <strong className="text-white">real, do-follow SEO backlink</strong> that passes
            page authority directly to their site — no redirect, no cloaking, no nofollow.
          </p>

          <div className="mt-6 relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-muted)]" />
            <input
              data-testid="directory-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by brand, keyword, description…"
              className="input-lux pl-9 w-full"
            />
          </div>
          <div className="mt-3 text-sm text-[var(--fg-muted)]" data-testid="directory-count">
            Showing <strong className="text-white">{filtered.length}</strong> of{" "}
            {listings.length} listed businesses
          </div>
        </header>

        {loading ? (
          <div
            className="flex items-center gap-2 text-[var(--fg-muted)]"
            data-testid="directory-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" /> Loading directory…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-[var(--fg-muted)]" data-testid="directory-empty">
            No matching listings yet.
          </div>
        ) : (
          <ol className="space-y-6" data-testid="directory-list">
            {filtered.map((l, idx) => {
              const href = ensureAbsoluteUrl(l.destination_url);
              const description =
                truncateWords(l.description || l.hover_slogan, 50) ||
                `${l.brand_name} — a business claiming a permanent block on the PixelEmpire AI grid.`;
              return (
                <li
                  key={l.id}
                  className="glass p-5 sm:p-6 border border-[var(--line-strong)] rounded-2xl"
                  data-testid={`directory-item-${l.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <h2
                        className="font-display text-2xl leading-tight text-white"
                        data-testid={`directory-brand-${l.id}`}
                      >
                        <span className="text-[var(--fg-muted)] font-mono text-sm mr-2">
                          #{idx + 1}
                        </span>
                        {l.brand_name || "Unnamed business"}
                      </h2>
                      <p
                        className="mt-2 text-sm sm:text-base text-[var(--fg-muted)] leading-relaxed"
                        data-testid={`directory-desc-${l.id}`}
                      >
                        {description}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2 items-center text-xs font-mono text-[var(--fg-muted)]">
                        <span>
                          Grid ({l.x}, {l.y})
                        </span>
                        <span>·</span>
                        <span>
                          {l.width}×{l.height} blocks
                        </span>
                        <span>·</span>
                        <span>{l.width * l.height * 100} pixels</span>
                        {l.category && (
                          <>
                            <span>·</span>
                            <span>{l.category}</span>
                          </>
                        )}
                      </div>
                      <div className="mt-4">
                        {/* Real dofollow anchor — no rel=nofollow, no
                            javascript wrapper. Google/Bing will crawl and
                            transmit link equity. Opens in same tab so search
                            bots follow the link naturally. */}
                        <a
                          href={href}
                          className="inline-flex items-center gap-1.5 text-cyan-300 hover:text-cyan-200 underline underline-offset-4 break-all"
                          data-testid={`directory-link-${l.id}`}
                        >
                          {href}
                          <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                        </a>
                      </div>
                    </div>
                    {l.image_url && (
                      <img
                        src={l.image_url}
                        alt={`${l.brand_name} logo`}
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-contain border border-[var(--line-strong)] flex-shrink-0 bg-white/5"
                        loading="lazy"
                        data-testid={`directory-logo-${l.id}`}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <div className="mt-14 glass p-6 rounded-2xl text-center">
          <h3 className="font-display text-2xl text-white">Want your startup listed here?</h3>
          <p className="mt-2 text-sm text-[var(--fg-muted)]">
            Claim your permanent spot on the digital grid for <strong>$0.50 per pixel</strong>{" "}
            ($50 per 10x10 block) and get a lifetime SEO backlink.
          </p>
          <Link
            to="/#explore"
            className="btn-primary mt-4 inline-flex"
            data-testid="directory-cta-claim"
          >
            Claim your block →
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
